import { ReactNode } from 'react';

import { isEmpty } from 'lodash-es';
import { useLocation } from 'react-router-dom';

import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CodeIcon from '@mui/icons-material/Code';
import ImportExport from '@mui/icons-material/ImportExport';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import SendIcon from '@mui/icons-material/Send';
import SettingsIcon from '@mui/icons-material/Settings';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import TimelineIcon from '@mui/icons-material/Timeline';
import { ClickAwayListener, List, Stack } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import { CSSObject, Theme, styled, useTheme } from '@mui/material/styles';

import usePrimaryNavStore from '@/components/shared/PrimaryNavDrawer/PrimaryNavStore';
import {
  NavItem,
  SPA_DOMAINS,
  getAnalyticsNavItems,
  getMessagingMenuOptions,
} from '@/components/shared/PrimaryNavDrawer/utils';
import { UserFeatureAccessType } from '@/generated';
import { useInAppNotification } from '@/providers/InAppNotificationProvider';
import { useSession } from '@/providers/SessionProvider/SessionProvider';
import { PERMISSIONS } from '@/utils/permissionTypes';
// TODO: feature_flag_cleanup: hasAccountPermissions - Remove ROLE_NAME_MAP import when FF is fully rolled out
import { ROLE_NAME_MAP } from '@/utils/roleUtils';

import PrimaryNavClientActions from './PrimaryNavClientActions';
import PrimaryNavLinks from './PrimaryNavLinks';
import PrimaryNavLogo from './PrimaryNavLogo';
import PrimaryNavNotificationWithFeed from './PrimaryNavNotification/PrimaryNavNotificationWithFeed';
import PrimaryNavSupport from './PrimaryNavSupport/PrimaryNavSupport';

const openedMixin = (theme: Theme): CSSObject => ({
  width: theme.primaryNav.navOpenWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.complex,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.complex,
  }),
  overflowX: 'hidden',
  width: theme.primaryNav.smallNavClosedWidth,
  [theme.breakpoints.up('sm')]: {
    width: theme.primaryNav.navClosedWidth,
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: theme.primaryNav.navOpenWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

const PrimaryNavDrawer = () => {
  const { isDrawerLocked, isDrawerOpen, setIsDrawerOpen } =
    usePrimaryNavStore();
  const { primaryNav } = useTheme();
  const { pathname } = useLocation();
  const { hasFeed: hasNotificationFeed } = useInAppNotification();
  const { currentClient } = useSession();
  const {
    enableAiFeatures,
    analyticsV2Access,
    hasDatasetsUi,
    hasNewAudiencesByDefault,
    hasPrograms,
    hasReportingDashboard,
    hasUnifiedMessagingNav,
  } = currentClient ?? {};

  const setupNavItems: NavItem[] = [
    {
      id: 'brand-kit',
      title: 'Brand Kit',
      route: '/brand-kit/guidelines',
      domains: null,
      featureAccess: UserFeatureAccessType.Release,
      isStaff: false,
      requiredPermission: PERMISSIONS.BRANDKIT_READ,
      // TODO: feature_flag_cleanup: hasAccountPermissions - Remove requiredRole when FF is fully rolled out
      requiredRole: ROLE_NAME_MAP.editor,
    },
    {
      id: 'settings',
      title: 'Settings',
      route: '/settings/tracking',
      domains: null,
      featureAccess: UserFeatureAccessType.Release,
      isStaff: false,
      requiredPermission: PERMISSIONS.SETTINGS_READ,
      // TODO: feature_flag_cleanup: hasAccountPermissions - Remove requiredRole when FF is fully rolled out
      requiredRole: ROLE_NAME_MAP.admin,
    },
  ];

  const iconMap: Record<string, ReactNode> = {
    // todo: there should be a way to pass in a string to an icon with <Icon>{icon name}</Icon>
    campaigns: <SendIcon sx={{ color: 'white' }} />,
    messaging: <SendIcon sx={{ color: 'white' }} />,
    journeys: <AccountTreeIcon sx={{ color: 'white' }} />,
    creatives: <PhotoLibraryIcon sx={{ color: 'white' }} />,
    ['audiences-new']: <SupervisorAccountIcon sx={{ color: 'white' }} />,
    audiences: <SupervisorAccountIcon sx={{ color: 'white' }} />,
    analytics: <TimelineIcon sx={{ color: 'white' }} />,
    'analytics-v2': <TimelineIcon sx={{ color: 'white' }} />,
    data: <ImportExport sx={{ color: 'white' }} />,
    settings: <SettingsIcon sx={{ color: 'white' }} />,
    setup: <SettingsIcon sx={{ color: 'white' }} />,
    'developer-tools': <CodeIcon sx={{ color: 'white' }} />,
    'ink-ai': <AutoAwesomeIcon sx={{ color: 'white' }} />,
  };
  const analyticsV2NavItems = getAnalyticsNavItems({
    hasPrograms: !!hasPrograms,
    hasReportingDashboard: !!hasReportingDashboard,
    hasUnifiedMessagingNav: !!hasUnifiedMessagingNav,
  });

  const dataNavItem: NavItem = {
    id: 'data',
    title: 'Data',
    domains: null,
    featureAccess: UserFeatureAccessType.Release,
    isStaff: false,
    requiredPermission: PERMISSIONS.DATA_READ,
    // TODO: feature_flag_cleanup: hasAccountPermissions - Remove requiredRole when FF is fully rolled out
    requiredRole: ROLE_NAME_MAP.editor,
    subItems: [
      {
        id: 'data-connections',
        title: 'Connections',
        route: '/data/connections',
        domains: null,
        featureAccess: UserFeatureAccessType.Release,
        isStaff: false,
        requiredPermission: PERMISSIONS.DATA_READ,
      },
      {
        id: 'tables',
        title: 'Tables',
        route: '/data/tables',
        domains: null,
        featureAccess: UserFeatureAccessType.Release,
        isStaff: false,
        requiredPermission: PERMISSIONS.DATA_READ,
      },
      {
        id: 'data-jobs',
        title: 'Jobs',
        route: '/data/jobs',
        domains: null,
        featureAccess: UserFeatureAccessType.Release,
        isStaff: false,
        requiredPermission: PERMISSIONS.DATA_READ,
      },
      {
        id: 'datasets',
        title: 'Datasets',
        route: '/data/datasets',
        domains: null,
        featureAccess: UserFeatureAccessType.Release,
        isStaff: false,
        requiredPermission: PERMISSIONS.DATA_READ,
        // TODO: feature_flag_cleanup: hasDatasetsUi
        visibilityCriteria: hasDatasetsUi,
      },
    ],
  };

  const setupNavItem: NavItem = {
    id: 'setup',
    title: 'Setup',
    domains: null,
    featureAccess: UserFeatureAccessType.Release,
    isStaff: false,
    requiredPermission: PERMISSIONS.BRANDKIT_READ,
    // TODO: feature_flag_cleanup: hasAccountPermissions - Remove requiredRole when FF is fully rolled out
    requiredRole: ROLE_NAME_MAP.editor,
    subItems: setupNavItems,
  };

  const navItems: NavItem[] = [
    {
      id: 'ink-ai',
      title: 'Ink AI',
      route: '/ink-ai',
      domains: null,
      featureAccess: UserFeatureAccessType.Release,
      isStaff: false,
      // Both flags required: hasFeatures checks hasCopilot, visibilityCriteria checks enableAiFeatures.
      // TODO: feature_flag_cleanup: hasCopilot
      hasFeatures: ['hasCopilot'],
      // TODO: feature_flag_cleanup: enableAiFeatures
      visibilityCriteria: !!enableAiFeatures,
    },
    ...getMessagingMenuOptions(!!hasPrograms),
    {
      id: 'creatives',
      title: 'Creatives',
      route: '/creatives',
      domains: null,
      featureAccess: UserFeatureAccessType.Release,
      isStaff: false,
      shouldSwitchClients: true,
      requiredPermission: PERMISSIONS.CREATIVES_READ,
    },
    {
      id: 'journeys',
      title: 'Journeys',
      route: '/journey',
      domains: null,
      featureAccess: UserFeatureAccessType.Release,
      isStaff: false,
      hasFeatures: ['hasJourneyBuilder'],
    },
    {
      id: 'audiences-new',
      title: 'Audiences',
      route: '/contacts/audiences-beta',
      domains: null,
      featureAccess: UserFeatureAccessType.Release,
      isStaff: false,
      visibilityCriteria: hasNewAudiencesByDefault,
      requiredPermission: PERMISSIONS.AUDIENCE_READ,
    },
    {
      id: 'audiences',
      title: 'Audiences',
      domains: null,
      featureAccess: UserFeatureAccessType.Release,
      isStaff: false,
      visibilityCriteria: !hasNewAudiencesByDefault,
      requiredPermission: PERMISSIONS.AUDIENCE_READ,
      subItems: [
        {
          id: 'audiences-beta',
          title: 'Audiences',
          titleDecoration: !hasNewAudiencesByDefault ? 'NEW!' : '',
          route: '/contacts/audiences-beta',
          domains: null,
          featureAccess: UserFeatureAccessType.Release,
          isStaff: false,
          requiredPermission: PERMISSIONS.AUDIENCE_READ,
        },
        {
          id: 'audiences',
          title: 'Audiences',
          route: '/contacts/audiences',
          header: 'Sunsetting soon',
          domains: SPA_DOMAINS,
          featureAccess: UserFeatureAccessType.Release,
          isStaff: false,
          requiredPermission: PERMISSIONS.AUDIENCE_READ,
        },
        {
          id: 'audience-groups',
          title: 'Audience Groups',
          route: '/contacts/audience-groups',
          domains: SPA_DOMAINS,
          featureAccess: UserFeatureAccessType.Release,
          isStaff: false,
          requiredPermission: PERMISSIONS.AUDIENCE_READ,
        },
      ],
    },
    ...(analyticsV2Access ? analyticsV2NavItems : []),
    dataNavItem,
    setupNavItem,
  ];

  const handleOnMouseEnterOfNav = () => {
    if (isDrawerLocked) return;
    setIsDrawerOpen(true);
  };

  const handleOnMouseLeaveOfNav = () => {
    if (isDrawerLocked) return;
    setIsDrawerOpen(false);
  };

  const isCurrentPage = (routePath: string) => {
    const paths = pathname.split('/');

    if (isEmpty(paths)) return false;

    return paths.at(-1) === routePath;
  };

  return (
    <Stack direction="row">
      <ClickAwayListener onClickAway={handleOnMouseLeaveOfNav}>
        <Drawer
          variant="permanent"
          open={isDrawerOpen}
          PaperProps={{
            sx: {
              backgroundColor: primaryNav.backgroundColor,
              border: 0,
              '&:hover': {
                boxShadow: '3px 0px 10px 0px rgba(0, 0, 0, 0.15)',
              },
            },
          }}
          onMouseEnter={handleOnMouseEnterOfNav}
          onMouseLeave={handleOnMouseLeaveOfNav}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            transition: (theme) =>
              `all ${theme.transitions.duration.complex}ms ${theme.transitions.easing.sharp}`,
          }}
          data-cy="primary-nav-drawer"
        >
          <List sx={{ marginTop: 0, marginBottom: 'auto', pt: 1 }}>
            <PrimaryNavLogo
              largeLogoPath="/davinci-logo-light.svg"
              smallLogoPath="/logo-small.svg"
            />
            <Stack component="ul" sx={{ px: 1, pt: 2, rowGap: 0.25 }}>
              <PrimaryNavLinks iconMap={iconMap} navItems={navItems} />
            </Stack>
          </List>
          <Stack sx={{ gap: 0.5, mb: 2 }}>
            <List
              component="div"
              sx={{
                display: 'flex',
                color: 'neutral00',
                flexDirection: 'column',
                gap: 0.5,
                px: 1,
                py: 0,
                width: '100%',
              }}
            >
              {hasNotificationFeed && (
                <PrimaryNavNotificationWithFeed
                  isCurrentPage={isCurrentPage('notifications')}
                  isDrawerOpen={isDrawerOpen}
                />
              )}

              <PrimaryNavSupport />
            </List>
            <PrimaryNavClientActions />
          </Stack>
        </Drawer>
      </ClickAwayListener>
    </Stack>
  );
};

export default PrimaryNavDrawer;
